import { createFileRoute } from '@tanstack/react-router';
import {
    Activity,
    ArrowLeft,
    Brain,
    CalendarClock,
    CircleAlert,
    ClipboardCheck,
    ClipboardList,
    Info,
    type LucideIcon,
    Maximize2,
    MessageCircle,
    Minimize2,
    Pause,
    Phone,
    Play,
    Reply,
    Send,
    Sparkles,
    UserX,
} from 'lucide-react';
import {
    Children,
    Fragment,
    type ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { SimulatorWorldVoiceReplay } from '@apps/web/components/admin/simulator_world_voice_replay';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@apps/web/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Spinner } from '@apps/web/components/ui/spinner';
import { toast } from '@apps/web/lib/app_notifications';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { cn } from '@apps/web/lib/utils';
import {
    getCaregiverSimDailyRunReplay,
    useCaregiverSimDailyPersonas,
    useCaregiverSimDailyRuns,
    useCaregiverSimMemoryEvents,
    useCaregiverSimWorldNow,
} from '@apps/web/swr_data_fetching/caregiver_sim_queries';
import {
    type CaregiverSimDailyRunReplayResponse,
    type CaregiverSimMemoryEventRead,
    type CaregiverSimWorldNowResponse,
} from '@libraries/typescript/api_client/schema_types';

export const Route = createFileRoute('/_app/admin/simulator-world')({
    component: SimulatorWorldPage,
    head: () => ({
        meta: [
            {
                title: 'Simulator World | Phoebe Admin',
            },
        ],
    }),
});

const SPEED_OPTIONS = ['0.5', '1', '2', '4', '8'] as const;
// Matches the neighborhood map image so the canvas has no letterboxing.
const MAP_WIDTH_PX = 1672;
const MAP_HEIGHT_PX = 941;
const MAP_ASPECT_RATIO = `${MAP_WIDTH_PX} / ${MAP_HEIGHT_PX}`;
const MAP_WIDTH_TO_HEIGHT_RATIO = `${MAP_WIDTH_PX / MAP_HEIGHT_PX}`;
const SHORT_MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
] as const;

const ENTRY_TYPE_BADGES: Record<
    string,
    { readonly icon: LucideIcon; readonly label: string }
> = {
    sms: { icon: MessageCircle, label: 'SMS' },
    voice_call: { icon: Phone, label: 'Call' },
    confirmation_sent: { icon: Send, label: 'Confirmation' },
    confirmation_response: { icon: ClipboardList, label: 'Response' },
    outreach_response: { icon: Reply, label: 'Outreach' },
    shift_outcome: { icon: ClipboardCheck, label: 'Shift' },
};

type SimProgressMessage = {
    readonly type: 'sim-world:progress';
    readonly fraction: number;
    readonly clockLabel: string;
    readonly playing: boolean;
};

type SimLoadedMessage = {
    readonly type: 'sim-world:loaded';
    readonly durationMs: number;
    readonly conversations: Array<{
        readonly identifier: string;
        readonly startFraction: number;
    }>;
};

type ReplayPersona = CaregiverSimDailyRunReplayResponse['personas'][number];
type ReplayDailyEvent =
    CaregiverSimDailyRunReplayResponse['daily_events'][number];
type ReplayEntry = CaregiverSimDailyRunReplayResponse['entries'][number];
type WorldNowEvent = CaregiverSimWorldNowResponse['recent_events'][number];
type WorldNowShift = CaregiverSimWorldNowResponse['active_shifts'][number];
type WorldMode = 'now' | 'replay';
type CaregiverWorldTone = 'attention' | 'active' | 'settled' | 'memory';
type WorldSnapshot = {
    readonly simulatedTime: string;
    readonly activeCaregivers: string;
    readonly voice: string;
    readonly recentEvent: string;
    readonly nextEvent: string;
};

// Run/replay statuses that mean a caregiver outcome went wrong and deserves a
// "Needs attention" signal, whether it surfaces as a daily event or a replay
// entry.
const ATTENTION_STATUSES = [
    'failed',
    'injection_failed',
    'not_extracted',
    'refill_failed',
    'unfilled',
];

type CaregiverWorldState = {
    readonly label: string;
    readonly evidence: string;
    readonly tone: CaregiverWorldTone;
    // Whether something noteworthy happened (an event, memory update, or
    // problem) — routine "responded"/"observed" states are not notable, so
    // the roster only badges caregivers worth a second look.
    readonly notable: boolean;
};

// The same sprite sheets `tools/sim_world/src/replay_director.ts` cycles
// through. We assign one by the caregiver's position in the full roster so
// each persona keeps a stable avatar. The map assigns sheets by position
// within a single run's cast, so a persona's roster avatar can differ from the
// map sprite when a run includes only part of the roster — that is acceptable
// for an at-a-glance avatar. Idle sheets are a 24-frame strip (16x32 per
// frame); frame 18 is the down-facing idle pose.
const CAST_SHEETS = ['adam', 'alex', 'bob'] as const;
const SPRITE_FRAME_WIDTH_PX = 16;
const SPRITE_FRAME_HEIGHT_PX = 32;
const SPRITE_FRAME_COUNT = 24;
const SPRITE_IDLE_FRAME = 18;

function normalizeSimWorldUrl(simWorldUrl: string): string {
    return simWorldUrl.endsWith('/') ? simWorldUrl : `${simWorldUrl}/`;
}

function castSheetForIndex(index: number): string {
    return CAST_SHEETS[index % CAST_SHEETS.length] ?? CAST_SHEETS[0];
}

function CharacterSprite({
    index,
    scale,
}: {
    readonly index: number;
    readonly scale: number;
}) {
    const simWorldUrl = getRuntimeConfig().simulatorWorldUrl;
    if (!simWorldUrl || index < 0) {
        return null;
    }
    const simWorldBaseUrl = normalizeSimWorldUrl(simWorldUrl);
    const sheet = castSheetForIndex(index);
    return (
        <div
            aria-hidden={true}
            className="shrink-0"
            style={{
                width: SPRITE_FRAME_WIDTH_PX * scale,
                height: SPRITE_FRAME_HEIGHT_PX * scale,
                backgroundImage: `url(${simWorldBaseUrl}assets/characters/${sheet}_idle_anim_16x16.png)`,
                backgroundPosition: `-${SPRITE_IDLE_FRAME * SPRITE_FRAME_WIDTH_PX * scale}px 0`,
                backgroundSize: `${SPRITE_FRAME_COUNT * SPRITE_FRAME_WIDTH_PX * scale}px ${SPRITE_FRAME_HEIGHT_PX * scale}px`,
                imageRendering: 'pixelated',
            }}
        />
    );
}

function buildLiveWorldPayload(
    personas: Array<ReplayPersona>,
    worldNow: CaregiverSimWorldNowResponse | null,
) {
    return {
        simulated_at: worldNow?.simulated_at ?? new Date().toISOString(),
        personas: personas.map((persona) => ({
            identifier: persona.identifier,
            name: persona.name,
        })),
        active_shifts: (worldNow?.active_shifts ?? []).map((shift) => ({
            persona_identifier: shift.persona_identifier,
            persona_name: shift.persona_name,
            patient_name: shift.patient_name,
            status: shift.status,
            start_at: shift.start_at,
            end_at: shift.end_at,
        })),
        recent_events: (worldNow?.recent_events ?? []).map((event) => ({
            timestamp: event.timestamp,
            event_type: event.event_type,
            label: event.label,
            detail: event.detail,
            persona_identifier: event.persona_identifier,
            persona_name: event.persona_name,
            shift_id: event.shift_id,
        })),
        upcoming_events: (worldNow?.upcoming_events ?? []).map((event) => ({
            timestamp: event.timestamp,
            event_type: event.event_type,
            label: event.label,
            detail: event.detail,
            persona_identifier: event.persona_identifier,
            persona_name: event.persona_name,
            shift_id: event.shift_id,
        })),
    };
}

function EntryBadge({ entryType }: { readonly entryType: string }) {
    const badge = ENTRY_TYPE_BADGES[entryType];
    if (!badge) {
        return <Badge variant="outline">{entryType}</Badge>;
    }
    return (
        <Badge variant="outline" className="gap-1">
            <badge.icon className="h-3 w-3" />
            {badge.label}
        </Badge>
    );
}

function formatLogTime(timestamp: string): string {
    return new Date(timestamp).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

function SimulatorWorldPage() {
    const simWorldUrl = getRuntimeConfig().simulatorWorldUrl;
    const { data: runsData, isLoading: runsLoading } =
        useCaregiverSimDailyRuns();
    const { data: dailyPersonasData } = useCaregiverSimDailyPersonas();
    const worldNowQuery = useCaregiverSimWorldNow();
    const rosterPersonas = useMemo(
        () => dailyPersonasData?.personas ?? [],
        [dailyPersonasData?.personas],
    );
    const liveWorldPayload = useMemo(
        () => buildLiveWorldPayload(rosterPersonas, worldNowQuery.data ?? null),
        [rosterPersonas, worldNowQuery.data],
    );
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [simReady, setSimReady] = useState(false);
    const [worldMode, setWorldMode] = useState<WorldMode>('now');
    const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
    const [replay, setReplay] =
        useState<CaregiverSimDailyRunReplayResponse | null>(null);
    const [replayLoading, setReplayLoading] = useState(false);
    const [mapExpanded, setMapExpanded] = useState(false);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState('1');
    const [fraction, setFraction] = useState(0);
    const [clockLabel, setClockLabel] = useState('');
    const [conversations, setConversations] = useState<
        SimLoadedMessage['conversations']
    >([]);
    const [selectedConversation, setSelectedConversation] = useState<
        string | null
    >(null);
    const [voicePersonaIdentifier, setVoicePersonaIdentifier] = useState<
        string | null
    >(null);
    const [inspectIdentifier, setInspectIdentifier] = useState<string | null>(
        null,
    );

    // Latest loaded replay + playback state, for re-sending if the iframe boots
    // (or reloads) after the user already picked a run.
    const latestReplayRef = useRef<CaregiverSimDailyRunReplayResponse | null>(
        null,
    );
    const worldModeRef = useRef(worldMode);
    worldModeRef.current = worldMode;
    const speedRef = useRef(speed);
    speedRef.current = speed;

    const simWorldOrigin = simWorldUrl
        ? new URL(simWorldUrl, window.location.origin).origin
        : null;

    const postToSim = useCallback(
        (message: object) => {
            if (!simWorldOrigin) {
                return;
            }
            iframeRef.current?.contentWindow?.postMessage(
                message,
                simWorldOrigin,
            );
        },
        [simWorldOrigin],
    );
    const setIframeRef = useCallback((node: HTMLIFrameElement | null) => {
        if (iframeRef.current === node) {
            return;
        }
        iframeRef.current = node;
        setSimReady(false);
    }, []);

    useEffect(() => {
        function onMessage(event: MessageEvent) {
            if (!simWorldOrigin || event.origin !== simWorldOrigin) {
                return;
            }
            const message = event.data as
                | SimProgressMessage
                | SimLoadedMessage
                | { readonly type: 'sim-world:ready' }
                | undefined;
            if (!message || typeof message !== 'object') {
                return;
            }
            if (message.type === 'sim-world:ready') {
                setSimReady(true);
                if (worldMode === 'now') {
                    postToSim({
                        type: 'sim-world:live',
                        live: liveWorldPayload,
                    });
                } else if (worldMode === 'replay' && latestReplayRef.current) {
                    postToSim({
                        type: 'sim-world:replay',
                        replay: latestReplayRef.current,
                    });
                    postToSim({
                        type: 'sim-world:speed',
                        multiplier: Number(speedRef.current),
                    });
                }
            } else if (message.type === 'sim-world:loaded') {
                setConversations(message.conversations);
            } else if (message.type === 'sim-world:progress') {
                setFraction(message.fraction);
                setClockLabel(message.clockLabel);
                setPlaying(message.playing);
            }
        }
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [liveWorldPayload, postToSim, simWorldOrigin, worldMode]);

    useEffect(() => {
        if (!simReady || worldMode !== 'now') {
            return;
        }
        postToSim({ type: 'sim-world:live', live: liveWorldPayload });
    }, [liveWorldPayload, postToSim, simReady, worldMode]);

    const personaNames = useMemo(
        () =>
            new Map(
                (replay?.personas ?? []).map((persona) => [
                    persona.identifier,
                    persona.name,
                ]),
            ),
        [replay?.personas],
    );

    // "Reliable Rachel Daily 2" -> "Rachel 2"
    const personaShortName = useCallback(
        (identifier: string | null | undefined) => {
            if (!identifier) {
                return '—';
            }
            const full = personaNames.get(identifier) ?? identifier;
            const words = full.trim().split(/\s+/);
            const last = words[words.length - 1] ?? '';
            const digit = /^\d+$/.test(last) ? ` ${last}` : '';
            return `${words[1] ?? words[0]}${digit}`;
        },
        [personaNames],
    );

    const transcriptEntries = selectedConversation
        ? (replay?.entries ?? []).filter(
              (entry) => entry.persona_identifier === selectedConversation,
          )
        : [];
    const dailyEvents = replay?.daily_events ?? [];
    const failures = replay?.failures ?? [];
    const replayVoiceCalls = useMemo(
        () =>
            (replay?.entries ?? []).filter(
                (entry) =>
                    entry.entry_type === 'voice_call' &&
                    entry.persona_identifier &&
                    Boolean(entry.recording_url || entry.transcript),
            ),
        [replay?.entries],
    );
    const voicePersonaIdentifiers = useMemo(() => {
        const identifiers = new Set<string>();
        for (const entry of replayVoiceCalls) {
            if (entry.persona_identifier) {
                identifiers.add(entry.persona_identifier);
            }
        }
        return identifiers;
    }, [replayVoiceCalls]);
    const worldSnapshot = useMemo(() => {
        const snapshot =
            worldMode === 'now'
                ? buildNowWorldSnapshot(
                      worldNowQuery.data ?? null,
                      worldNowQuery.isLoading,
                  )
                : buildWorldSnapshot(replay, fraction, personaShortName);

        return worldMode === 'replay' && replay
            ? {
                  ...snapshot,
                  voice: formatVoiceReplayMetric(replayVoiceCalls.length),
              }
            : snapshot;
    }, [
        fraction,
        personaShortName,
        replay,
        replayVoiceCalls.length,
        worldMode,
        worldNowQuery.data,
        worldNowQuery.isLoading,
    ]);
    // Persistent memory is only fetched for the caregiver currently being
    // inspected in the modal, not every persona on the map.
    const inspectMemoryQuery = useCaregiverSimMemoryEvents(
        inspectIdentifier,
        null,
        inspectIdentifier !== null,
    );
    const inspectMemoryEvents = useMemo(
        () =>
            sortMemoryEventsDesc(
                uniqueMemoryEvents([
                    ...(inspectMemoryQuery.data?.memory_events ?? []),
                    ...(inspectMemoryQuery.data?.reflections ?? []),
                ]),
            ),
        [inspectMemoryQuery.data],
    );

    // Guards against an older in-flight load resolving after a newer one.
    const loadSequenceRef = useRef(0);

    const loadReplay = useCallback(
        async (
            runId: string,
            options: { readonly activateReplay?: boolean } = {},
        ) => {
            const activateReplay = options.activateReplay ?? true;
            const sequence = ++loadSequenceRef.current;
            setReplayLoading(true);
            if (activateReplay) {
                // Stop and unload the previous run immediately so the map
                // never animates a different run than the picker shows.
                postToSim({ type: 'sim-world:clear' });
            }
            setReplay(null);
            latestReplayRef.current = null;
            setSelectedConversation(null);
            setVoicePersonaIdentifier(null);
            setInspectIdentifier(null);
            setConversations([]);
            setFraction(0);
            setClockLabel('');
            setPlaying(false);
            try {
                const response = await getCaregiverSimDailyRunReplay(runId);
                if (sequence !== loadSequenceRef.current) {
                    return;
                }
                setReplay(response);
                latestReplayRef.current = response;
                setFraction(0);
                if (activateReplay || worldModeRef.current === 'replay') {
                    postToSim({
                        type: 'sim-world:replay',
                        replay: response,
                    });
                    postToSim({
                        type: 'sim-world:speed',
                        multiplier: Number(speedRef.current),
                    });
                }
                // Loads paused — playback starts when the user hits play.
                setPlaying(false);
            } catch (error) {
                if (sequence !== loadSequenceRef.current) {
                    return;
                }
                toast.error(
                    error instanceof Error
                        ? error.message
                        : 'Failed to load replay',
                    { persistNotification: false },
                );
            } finally {
                if (sequence === loadSequenceRef.current) {
                    setReplayLoading(false);
                }
            }
        },
        [postToSim],
    );

    // Default to the most recent run so the roster shows recent activity right
    // away; picking an earlier date from the picker gives a calmer, scoped view.
    useEffect(() => {
        const latest = (runsData?.runs ?? [])[0];
        if (selectedRunId === null && latest) {
            setSelectedRunId(latest.id);
            void loadReplay(latest.id, { activateReplay: false });
        }
    }, [runsData, selectedRunId, loadReplay]);

    if (!simWorldUrl) {
        return (
            <div className="p-6">
                <h1 className="text-xl font-semibold">Simulator World</h1>
                <p className="text-muted-foreground mt-2 text-sm">
                    The simulator world app is not configured for this
                    environment. Run it locally with{' '}
                    <code>pnpm --filter @phoebe/sim-world dev</code> and set
                    simulatorWorldUrl in the runtime config.
                </p>
            </div>
        );
    }

    const runs = runsData?.runs ?? [];
    const simWorldBaseUrl = normalizeSimWorldUrl(simWorldUrl);
    const simWorldIframeUrl = `${simWorldBaseUrl}index.html?embed=1`;

    return (
        <div className="flex h-full flex-col gap-3 p-4">
            <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-xl font-semibold">Simulator World</h1>
                <div className="bg-background inline-flex rounded-md border p-0.5">
                    <Button
                        variant={worldMode === 'now' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 gap-1"
                        onClick={() => {
                            setWorldMode('now');
                            setSelectedConversation(null);
                            postToSim({
                                type: 'sim-world:live',
                                live: liveWorldPayload,
                            });
                        }}
                    >
                        <Activity className="h-4 w-4" />
                        Now
                    </Button>
                    <Button
                        variant={worldMode === 'replay' ? 'secondary' : 'ghost'}
                        size="sm"
                        className="h-8 gap-1"
                        onClick={() => {
                            setWorldMode('replay');
                            if (latestReplayRef.current) {
                                postToSim({
                                    type: 'sim-world:replay',
                                    replay: latestReplayRef.current,
                                });
                                postToSim({
                                    type: 'sim-world:speed',
                                    multiplier: Number(speed),
                                });
                            }
                        }}
                    >
                        <Play className="h-4 w-4" />
                        Replay
                    </Button>
                </div>
                {worldMode === 'replay' ? (
                    <Fragment>
                        <Select
                            value={selectedRunId ?? undefined}
                            onValueChange={(runId) => {
                                setWorldMode('replay');
                                setSelectedRunId(runId);
                                void loadReplay(runId, {
                                    activateReplay: true,
                                });
                            }}
                        >
                            <SelectTrigger className="w-72">
                                <SelectValue
                                    placeholder={
                                        runsLoading
                                            ? 'Loading runs…'
                                            : runs.length === 0
                                              ? 'No daily runs found'
                                              : 'Pick a daily run'
                                    }
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {runs.map((run) => (
                                    <SelectItem key={run.id} value={run.id}>
                                        {run.local_date} · {run.status} ·{' '}
                                        {run.filled_count}/
                                        {run.shifts_attempted_count} filled
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={
                                !simReady || replay === null || replayLoading
                            }
                            onClick={() => {
                                setWorldMode('replay');
                                postToSim({
                                    type: playing
                                        ? 'sim-world:pause'
                                        : 'sim-world:play',
                                });
                            }}
                        >
                            {replayLoading ? (
                                <Spinner />
                            ) : playing ? (
                                <Pause className="h-4 w-4" />
                            ) : (
                                <Play className="h-4 w-4" />
                            )}
                        </Button>
                        <Select
                            value={speed}
                            onValueChange={(value) => {
                                setSpeed(value);
                                postToSim({
                                    type: 'sim-world:speed',
                                    multiplier: Number(value),
                                });
                            }}
                        >
                            <SelectTrigger className="w-24">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {SPEED_OPTIONS.map((option) => (
                                    <SelectItem key={option} value={option}>
                                        {option}x
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <input
                            type="range"
                            min={0}
                            max={1000}
                            value={Math.round(fraction * 1000)}
                            disabled={replay === null}
                            className="w-64"
                            onChange={(event) => {
                                const next = Number(event.target.value) / 1000;
                                setWorldMode('replay');
                                setFraction(next);
                                postToSim({
                                    type: 'sim-world:seek',
                                    fraction: next,
                                });
                            }}
                        />
                        {clockLabel ? (
                            <span className="text-muted-foreground text-sm tabular-nums">
                                {clockLabel}
                            </span>
                        ) : null}
                    </Fragment>
                ) : null}
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
                <WorldStatusStrip snapshot={worldSnapshot} />
                <div
                    aria-label={
                        mapExpanded ? 'Expanded simulator world map' : undefined
                    }
                    aria-modal={mapExpanded ? true : undefined}
                    role={mapExpanded ? 'dialog' : undefined}
                    className={cn(
                        mapExpanded
                            ? 'fixed inset-0 z-50 flex items-center justify-center p-3'
                            : 'relative mx-auto',
                    )}
                    style={
                        mapExpanded
                            ? undefined
                            : {
                                  aspectRatio: MAP_ASPECT_RATIO,
                                  width: `min(100%, calc(78vh * ${MAP_WIDTH_TO_HEIGHT_RATIO}))`,
                              }
                    }
                >
                    {mapExpanded ? (
                        <button
                            type="button"
                            aria-label="Close expanded map"
                            className="absolute inset-0 cursor-default bg-black/60"
                            onClick={() => setMapExpanded(false)}
                        />
                    ) : null}
                    <div
                        className={cn(
                            'relative',
                            mapExpanded
                                ? 'bg-background z-10 rounded-lg p-3 shadow-2xl'
                                : 'h-full w-full',
                        )}
                    >
                        <div
                            className="relative h-full w-full overflow-hidden rounded-md"
                            style={
                                mapExpanded
                                    ? {
                                          aspectRatio: MAP_ASPECT_RATIO,
                                          width: `min(calc(100vw - 3rem), calc((100vh - 3rem) * ${MAP_WIDTH_TO_HEIGHT_RATIO}))`,
                                      }
                                    : undefined
                            }
                        >
                            <iframe
                                ref={setIframeRef}
                                src={simWorldIframeUrl}
                                title="Simulator World"
                                className="h-full w-full"
                            />
                            <Button
                                type="button"
                                variant="secondary"
                                size="icon"
                                className="absolute top-2 right-2 h-8 w-8 shadow-sm"
                                title={
                                    mapExpanded ? 'Collapse map' : 'Expand map'
                                }
                                onClick={() =>
                                    setMapExpanded((expanded) => !expanded)
                                }
                            >
                                {mapExpanded ? (
                                    <Minimize2 className="h-4 w-4" />
                                ) : (
                                    <Maximize2 className="h-4 w-4" />
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
                <CaregiverRoster
                    personas={rosterPersonas}
                    replay={replay}
                    onInspect={(identifier) => {
                        setInspectIdentifier(identifier);
                        if (voicePersonaIdentifiers.has(identifier)) {
                            setVoicePersonaIdentifier(identifier);
                        }
                    }}
                />
                {worldMode === 'replay' && conversations.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {conversations.map((conversation) => (
                            <button
                                key={conversation.identifier}
                                type="button"
                                className={cn(
                                    'hover:bg-accent inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                                    selectedConversation ===
                                        conversation.identifier
                                        ? 'bg-accent font-medium'
                                        : 'text-muted-foreground',
                                )}
                                onClick={() => {
                                    if (
                                        selectedConversation ===
                                        conversation.identifier
                                    ) {
                                        setSelectedConversation(null);
                                        return;
                                    }
                                    if (
                                        voicePersonaIdentifiers.has(
                                            conversation.identifier,
                                        )
                                    ) {
                                        setVoicePersonaIdentifier(
                                            conversation.identifier,
                                        );
                                    }
                                    setSelectedConversation(
                                        conversation.identifier,
                                    );
                                    setFraction(conversation.startFraction);
                                    setWorldMode('replay');
                                    postToSim({
                                        type: 'sim-world:seek',
                                        fraction: conversation.startFraction,
                                    });
                                    postToSim({ type: 'sim-world:play' });
                                    setPlaying(true);
                                }}
                            >
                                <MessageCircle className="h-3 w-3" />
                                {personaShortName(conversation.identifier)}
                            </button>
                        ))}
                    </div>
                ) : null}
                <SimulatorWorldVoiceReplay
                    key={selectedRunId ?? 'no-run'}
                    personas={rosterPersonas}
                    replayVoiceCalls={replayVoiceCalls}
                    selectedIdentifier={voicePersonaIdentifier}
                    onSelectedIdentifierChange={setVoicePersonaIdentifier}
                />
                <div className="min-h-[240px] overflow-y-auto rounded-md border">
                    {worldMode === 'now' ? (
                        <WorldNowOverview
                            worldNow={worldNowQuery.data ?? null}
                            loading={worldNowQuery.isLoading}
                            errorMessage={formatWorldNowErrorMessage(
                                worldNowQuery.error,
                            )}
                        />
                    ) : replay === null ? (
                        <p className="text-muted-foreground p-3 text-sm">
                            Pick a daily run, then press play. Click a caregiver
                            to inspect their state, or a conversation to read
                            the dialogue.
                        </p>
                    ) : selectedConversation === null ? (
                        <ReplayOverviewTable
                            failures={failures}
                            dailyEvents={dailyEvents}
                            personaShortName={personaShortName}
                        />
                    ) : (
                        <ReplayConversationTable
                            entries={transcriptEntries}
                            personaShortName={personaShortName}
                            onBack={() => setSelectedConversation(null)}
                        />
                    )}
                </div>
            </div>
            <CaregiverInspectModal
                persona={
                    rosterPersonas.find(
                        (candidate) =>
                            candidate.identifier === inspectIdentifier,
                    ) ?? null
                }
                personaIndex={rosterPersonas.findIndex(
                    (candidate) => candidate.identifier === inspectIdentifier,
                )}
                replay={replay}
                memoryEvents={inspectMemoryEvents}
                memoryLoading={inspectMemoryQuery.isLoading}
                onClose={() => setInspectIdentifier(null)}
            />
        </div>
    );
}

function WorldStatusStrip({ snapshot }: { readonly snapshot: WorldSnapshot }) {
    return (
        <div
            data-testid="sim-world-status-strip"
            className="bg-background grid gap-2 border-y px-3 py-2 md:grid-cols-5"
        >
            <WorldStatusMetric
                icon={CalendarClock}
                label="Sim time"
                value={snapshot.simulatedTime}
            />
            <WorldStatusMetric
                icon={Activity}
                label="Active now"
                value={snapshot.activeCaregivers}
            />
            <WorldStatusMetric
                icon={Phone}
                label="Voice"
                value={snapshot.voice}
            />
            <WorldStatusMetric
                icon={ClipboardCheck}
                label="Recent event"
                value={snapshot.recentEvent}
            />
            <WorldStatusMetric
                icon={CalendarClock}
                label="Next expected"
                value={snapshot.nextEvent}
            />
        </div>
    );
}

function WorldStatusMetric({
    icon: Icon,
    label,
    value,
}: {
    readonly icon: LucideIcon;
    readonly label: string;
    readonly value: string;
}) {
    return (
        <div className="flex min-w-0 items-center gap-2">
            <Icon className="text-muted-foreground h-4 w-4 shrink-0" />
            <div className="min-w-0">
                <div className="text-muted-foreground text-[11px] font-medium">
                    {label}
                </div>
                <div className="truncate text-sm font-medium">{value}</div>
            </div>
        </div>
    );
}

function formatWorldNowErrorMessage(error: unknown): string | null {
    if (!error) {
        return null;
    }
    if (error instanceof Error && error.message.trim().length > 0) {
        return error.message;
    }
    if (typeof error === 'string' && error.trim().length > 0) {
        return error;
    }
    if (typeof error === 'object') {
        const errorRecord = error as Record<string, unknown>;
        const message = errorRecord['message'];
        if (typeof message === 'string' && message.trim().length > 0) {
            return message;
        }
        const detail = errorRecord['detail'];
        if (typeof detail === 'string' && detail.trim().length > 0) {
            return detail;
        }
    }
    return 'Unknown error';
}

function WorldNowOverview({
    worldNow,
    loading,
    errorMessage,
}: {
    readonly worldNow: CaregiverSimWorldNowResponse | null;
    readonly loading: boolean;
    readonly errorMessage: string | null;
}) {
    if (errorMessage) {
        return (
            <p className="text-muted-foreground p-3 text-sm">
                Could not load current simulator state: {errorMessage}
            </p>
        );
    }
    if (!worldNow) {
        return (
            <p className="text-muted-foreground p-3 text-sm">
                {loading
                    ? 'Loading current simulator state…'
                    : 'No current simulator state found.'}
            </p>
        );
    }

    return (
        <div className="grid gap-3 p-3 lg:grid-cols-4">
            <WorldNowSection
                title="Active shifts"
                empty="No active shifts right now"
            >
                {worldNow.active_shifts.map((shift) => (
                    <WorldNowShiftRow
                        key={shift.shift_id}
                        shift={shift}
                        timeZone={worldNow.organization_timezone}
                    />
                ))}
            </WorldNowSection>
            <WorldNowSection title="Recent events" empty="No recent events">
                {worldNow.recent_events.map((event, index) => (
                    <WorldNowEventRow
                        key={`${event.event_type}-${event.timestamp}-${event.shift_id ?? 'none'}-${index}`}
                        event={event}
                        timeZone={worldNow.organization_timezone}
                    />
                ))}
            </WorldNowSection>
            <WorldNowSection title="Voice" empty="No voice status">
                <WorldNowVoiceCard voice={worldNow.voice} />
            </WorldNowSection>
            <WorldNowSection title="Next expected" empty="No upcoming events">
                {worldNow.upcoming_events.map((event, index) => (
                    <WorldNowEventRow
                        key={`${event.event_type}-${event.timestamp}-${event.shift_id ?? 'none'}-${index}`}
                        event={event}
                        timeZone={worldNow.organization_timezone}
                    />
                ))}
            </WorldNowSection>
        </div>
    );
}

function WorldNowVoiceCard({
    voice,
}: {
    readonly voice: CaregiverSimWorldNowResponse['voice'];
}) {
    const toneClass =
        voice.status === 'active' || voice.status === 'configured'
            ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/50'
            : 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/50';
    return (
        <div className={cn('rounded-md border p-2', toneClass)}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                        {voice.label}
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs">
                        {voice.detail}
                    </p>
                </div>
                <Badge variant="outline">
                    {voice.configured ? 'Ready' : 'SMS-only'}
                </Badge>
            </div>
        </div>
    );
}

function WorldNowSection({
    title,
    empty,
    children,
}: {
    readonly title: string;
    readonly empty: string;
    readonly children: ReactNode;
}) {
    const childCount = Children.count(children);
    return (
        <section className="min-w-0">
            <h2 className="mb-2 text-sm font-semibold">{title}</h2>
            {childCount > 0 ? (
                <div className="space-y-2">{children}</div>
            ) : (
                <p className="text-muted-foreground rounded-md border border-dashed p-3 text-sm">
                    {empty}
                </p>
            )}
        </section>
    );
}

function WorldNowShiftRow({
    shift,
    timeZone,
}: {
    readonly shift: WorldNowShift;
    readonly timeZone: string;
}) {
    return (
        <div className="bg-background rounded-md border p-2">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                        {shift.persona_name ?? 'Open shift'}
                    </p>
                    <p className="text-muted-foreground truncate text-xs">
                        {shift.patient_name}
                    </p>
                </div>
                <Badge variant="outline">{shift.status}</Badge>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
                {formatCompactZonedDateTime(shift.start_at, timeZone)} -{' '}
                {formatCompactZonedDateTime(shift.end_at, timeZone)}
            </p>
        </div>
    );
}

function WorldNowEventRow({
    event,
    timeZone,
}: {
    readonly event: WorldNowEvent;
    readonly timeZone: string;
}) {
    return (
        <div className="bg-background rounded-md border p-2">
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <p className="truncate text-sm font-medium">
                        {event.label}
                    </p>
                    {event.detail ? (
                        <p className="text-muted-foreground truncate text-xs">
                            {event.detail}
                        </p>
                    ) : null}
                </div>
                <Badge variant="outline">
                    {worldNowEventLabel(event.event_type)}
                </Badge>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">
                {formatCompactZonedDateTime(event.timestamp, timeZone)}
            </p>
        </div>
    );
}

function worldNowEventLabel(eventType: WorldNowEvent['event_type']): string {
    if (eventType === 'shift_start') {
        return 'Start';
    }
    if (eventType === 'shift_end') {
        return 'End';
    }
    if (eventType === 'clocked_in') {
        return 'Clock in';
    }
    if (eventType === 'clocked_out') {
        return 'Clock out';
    }
    if (eventType === 'world_tick') {
        return 'Tick';
    }
    if (eventType === 'quiet_period') {
        return 'Quiet';
    }
    if (eventType === 'shift_attention') {
        return 'Attention';
    }
    if (eventType === 'outreach_ready') {
        return 'Outreach';
    }
    if (eventType === 'voice_ready') {
        return 'Voice';
    }
    return eventType
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
}

const SIMULATOR_WORLD_NOTE_FAILURE_CODES = new Set([
    'callout_outcome_timeout',
    'shift_confirmation_outcome_timeout',
    'voice_flows_recorded_skipped',
]);

function simulatorFailureSeverity(code: string): 'error' | 'note' {
    if (code.endsWith('_skipped')) {
        return 'note';
    }
    return SIMULATOR_WORLD_NOTE_FAILURE_CODES.has(code) ? 'note' : 'error';
}

function ReplayOverviewTable({
    failures,
    dailyEvents,
    personaShortName,
}: {
    readonly failures: CaregiverSimDailyRunReplayResponse['failures'];
    readonly dailyEvents: Array<ReplayDailyEvent>;
    readonly personaShortName: (
        identifier: string | null | undefined,
    ) => string;
}) {
    return (
        <div className="p-3">
            <p className="text-muted-foreground text-sm">
                Click a conversation above to read its dialogue.
            </p>
            {failures.length > 0 ? (
                <table className="mt-2 w-full text-sm">
                    <tbody>
                        {failures.map((failure, index) => {
                            const severity = simulatorFailureSeverity(
                                failure.code,
                            );
                            return (
                                <tr
                                    key={`failure-${index}`}
                                    className="border-b last:border-b-0"
                                >
                                    <td className="w-32 px-2 py-1.5 align-top whitespace-nowrap">
                                        {severity === 'note' ? (
                                            <Badge
                                                variant="outline"
                                                className="gap-1"
                                            >
                                                <Info className="h-3 w-3" />
                                                Note
                                            </Badge>
                                        ) : (
                                            <Badge
                                                variant="outline"
                                                className="gap-1 text-red-600 dark:text-red-400"
                                            >
                                                <CircleAlert className="h-3 w-3" />
                                                Error
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="w-40 px-2 py-1.5 align-top text-xs font-medium whitespace-nowrap">
                                        {failure.code}
                                    </td>
                                    <td className="text-muted-foreground px-2 py-1.5 align-top text-xs">
                                        <div>{failure.detail ?? ''}</div>
                                        {failure.causal_stage ? (
                                            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                                                <span>
                                                    Stage{' '}
                                                    <span className="text-foreground font-mono">
                                                        {failure.causal_stage}
                                                    </span>
                                                </span>
                                                {failure.stage_observed_at ? (
                                                    <span>
                                                        Observed{' '}
                                                        <span className="text-foreground tabular-nums">
                                                            {formatCompactDateTime(
                                                                failure.stage_observed_at,
                                                            )}
                                                        </span>
                                                    </span>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            ) : null}
            {dailyEvents.length > 0 ? (
                <table className="mt-2 w-full text-sm">
                    <tbody>
                        {dailyEvents.map((event, index) => (
                            <tr
                                key={`daily-${index}`}
                                className="border-b last:border-b-0"
                            >
                                <td className="w-32 px-2 py-1.5 align-top whitespace-nowrap">
                                    <Badge variant="outline" className="gap-1">
                                        {event.event_type ===
                                        'memory_change' ? (
                                            <Brain className="h-3 w-3" />
                                        ) : (
                                            <UserX className="h-3 w-3" />
                                        )}
                                        {event.event_type === 'memory_change'
                                            ? 'Memory'
                                            : 'Callout'}
                                    </Badge>
                                </td>
                                <td className="w-40 px-2 py-1.5 align-top text-xs font-medium whitespace-nowrap">
                                    {personaShortName(event.persona_identifier)}
                                </td>
                                <td className="text-muted-foreground px-2 py-1.5 align-top text-xs">
                                    {event.detail ?? ''}
                                    {event.status ? ` · ${event.status}` : ''}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : null}
        </div>
    );
}

function ReplayConversationTable({
    entries,
    personaShortName,
    onBack,
}: {
    readonly entries: Array<ReplayEntry>;
    readonly personaShortName: (
        identifier: string | null | undefined,
    ) => string;
    readonly onBack: () => void;
}) {
    return (
        <div>
            <Button variant="ghost" size="sm" className="m-1" onClick={onBack}>
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to events
            </Button>
            <table className="w-full text-sm">
                <tbody>
                    {entries.map((entry, index) => (
                        <tr
                            key={`${entry.timestamp}-${index}`}
                            className="border-b last:border-b-0"
                        >
                            <td className="text-muted-foreground w-24 px-3 py-1.5 align-top text-xs whitespace-nowrap tabular-nums">
                                {formatLogTime(entry.timestamp)}
                            </td>
                            <td className="w-32 px-2 py-1.5 align-top whitespace-nowrap">
                                <EntryBadge entryType={entry.entry_type} />
                            </td>
                            <td className="w-40 px-2 py-1.5 align-top text-xs font-medium whitespace-nowrap">
                                {entry.direction === 'outbound' ||
                                entry.entry_type === 'confirmation_sent'
                                    ? 'Phoebe'
                                    : personaShortName(
                                          entry.persona_identifier,
                                      )}
                            </td>
                            <td className="text-muted-foreground px-2 py-1.5 align-top text-xs">
                                {entry.body ?? entry.status ?? ''}
                                {entry.recording_url ? (
                                    <audio
                                        controls={true}
                                        preload="none"
                                        src={entry.recording_url}
                                        className="mt-1 h-8 w-full max-w-md"
                                    />
                                ) : null}
                                {entry.transcript ? (
                                    <details className="mt-1">
                                        <summary className="cursor-pointer">
                                            Transcript
                                        </summary>
                                        <pre className="mt-1 text-xs whitespace-pre-wrap">
                                            {entry.transcript}
                                        </pre>
                                    </details>
                                ) : null}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function CaregiverRoster({
    personas,
    replay,
    onInspect,
}: {
    readonly personas: Array<ReplayPersona>;
    readonly replay: CaregiverSimDailyRunReplayResponse | null;
    readonly onInspect: (identifier: string) => void;
}) {
    if (personas.length === 0) {
        return null;
    }
    return (
        <div
            data-testid="sim-world-caregiver-roster"
            className="bg-background rounded-md border p-3"
        >
            <div className="mb-2 flex items-center gap-2">
                <Activity className="text-muted-foreground h-4 w-4" />
                <h2 className="text-sm font-semibold">Caregivers</h2>
                <span className="text-muted-foreground text-xs">
                    {replay === null
                        ? 'Click a caregiver to inspect their state.'
                        : 'Click a caregiver to inspect their state for this run.'}
                </span>
            </div>
            <div className="flex flex-wrap gap-2">
                {personas.map((persona, index) => {
                    // Without a loaded run the roster is a calm directory; a
                    // selected run badges whoever had noteworthy activity.
                    const personaState = replay
                        ? buildCaregiverWorldState({
                              entries: (replay.entries ?? []).filter(
                                  (entry) =>
                                      entry.persona_identifier ===
                                      persona.identifier,
                              ),
                              dailyEvents: (replay.daily_events ?? []).filter(
                                  (event) =>
                                      event.persona_identifier ===
                                      persona.identifier,
                              ),
                              memoryEvents: [],
                          })
                        : null;
                    return (
                        <button
                            key={persona.identifier}
                            type="button"
                            className="hover:bg-accent hover:border-border flex w-44 items-center gap-2 rounded-lg border px-3 py-2 text-left"
                            onClick={() => onInspect(persona.identifier)}
                        >
                            <CharacterSprite index={index} scale={1.5} />
                            <div className="flex min-w-0 flex-col gap-1">
                                <span className="truncate text-sm font-medium">
                                    {persona.name}
                                </span>
                                {personaState?.notable ? (
                                    <span
                                        className={cn(
                                            'inline-flex w-fit rounded-full px-1.5 py-0.5 text-[10px] font-medium',
                                            caregiverWorldToneClassName(
                                                personaState.tone,
                                            ),
                                        )}
                                    >
                                        {personaState.label}
                                    </span>
                                ) : null}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function CaregiverInspectModal({
    persona,
    personaIndex,
    replay,
    memoryEvents,
    memoryLoading,
    onClose,
}: {
    readonly persona: ReplayPersona | null;
    readonly personaIndex: number;
    readonly replay: CaregiverSimDailyRunReplayResponse | null;
    readonly memoryEvents: Array<CaregiverSimMemoryEventRead>;
    readonly memoryLoading: boolean;
    readonly onClose: () => void;
}) {
    const entries = sortEntriesDesc(
        (replay?.entries ?? []).filter(
            (entry) => entry.persona_identifier === persona?.identifier,
        ),
    );
    // Recent events are real lifecycle events (accepted/declined a shift,
    // shift outcomes, calls) plus run-level events — not the raw chat log.
    const eventEntries = entries.filter((entry) =>
        EVENT_ENTRY_TYPES.has(entry.entry_type),
    );
    const runEvents = (replay?.daily_events ?? []).filter(
        (event) => event.persona_identifier === persona?.identifier,
    );
    const state = buildCaregiverWorldState({
        entries,
        dailyEvents: runEvents,
        memoryEvents,
    });
    // A "Needs attention" state comes from run/replay signals and is
    // memory-independent, so show it right away; any other mood can depend on
    // memory, so hold a neutral state until the fetch resolves rather than
    // briefly showing a run-only mood that flips once memory arrives.
    const feelingPending = memoryLoading && state.tone !== 'attention';
    const reflections = memoryEvents.filter(
        (event) => event.kind === 'thought',
    );
    // The persona's built-in personality — how it behaves and talks. Falls
    // back to the descriptor in its name if no summary is available.
    const personality =
        persona?.description ??
        (persona ? personaDescriptor(persona.name) : null);
    const roles = persona?.roles ?? [];
    const subtitle = [roleLabel(roles), persona?.identifier]
        .filter(Boolean)
        .join(' · ');

    return (
        <Dialog
            open={persona !== null}
            onOpenChange={(open) => {
                if (!open) {
                    onClose();
                }
            }}
        >
            {persona ? (
                <DialogContent
                    data-testid="sim-world-caregiver-inspect-modal"
                    className="max-h-[85vh] overflow-y-auto"
                >
                    <DialogHeader>
                        <div className="flex items-center gap-3">
                            <CharacterSprite index={personaIndex} scale={2} />
                            <div className="flex min-w-0 flex-col gap-1">
                                <DialogTitle>{persona.name}</DialogTitle>
                                <DialogDescription className="font-mono">
                                    {subtitle}
                                </DialogDescription>
                            </div>
                        </div>
                    </DialogHeader>
                    <section>
                        <div className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-semibold">
                            <Activity className="h-3.5 w-3.5" />
                            Feeling
                        </div>
                        {feelingPending ? (
                            <div className="text-muted-foreground flex items-center gap-2 rounded-md border p-3 text-sm">
                                <Spinner />
                                Reading recent memory…
                            </div>
                        ) : (
                            <div
                                className={cn(
                                    'rounded-md border p-3',
                                    caregiverWorldToneClassName(state.tone),
                                )}
                            >
                                <span className="text-sm font-medium">
                                    {state.label}
                                </span>
                                <p className="mt-1 text-xs">{state.evidence}</p>
                            </div>
                        )}
                    </section>
                    <PanelSection
                        icon={Sparkles}
                        title="Personality"
                        emptyText="No personality recorded for this caregiver."
                    >
                        {personality ? (
                            <p className="text-foreground/80 text-xs">
                                {personality}
                            </p>
                        ) : null}
                    </PanelSection>
                    <PanelSection
                        icon={Brain}
                        title="How they think"
                        loading={memoryLoading}
                        emptyText="No reflections stored for this caregiver yet."
                    >
                        {reflections.slice(0, 5).map((event) => (
                            <MemoryEventRow key={event.id} event={event} />
                        ))}
                    </PanelSection>
                    <PanelSection
                        icon={CalendarClock}
                        title="Recent events"
                        emptyText="No shift or clock events recorded for this caregiver."
                    >
                        {runEvents.slice(0, 4).map((event, index) => (
                            <RunEventRow
                                key={`run-${event.event_type}-${event.shift_id ?? index}`}
                                event={event}
                            />
                        ))}
                        {eventEntries.slice(0, 6).map((entry, index) => (
                            <EventEntryRow
                                key={`event-${entry.timestamp}-${index}`}
                                entry={entry}
                            />
                        ))}
                    </PanelSection>
                </DialogContent>
            ) : null}
        </Dialog>
    );
}

function roleLabel(roles: Array<string>): string {
    return roles.map((role) => role.toUpperCase()).join(', ');
}

// "Methodical Maya" -> "Methodical"; the descriptor before the first name is
// the persona's built-in personality.
function personaDescriptor(name: string): string {
    const words = name.trim().split(/\s+/);
    return words.length > 1 ? words.slice(0, -1).join(' ') : name;
}

// Replay entry types that represent real caregiver lifecycle events, as
// opposed to the back-and-forth SMS chat.
const EVENT_ENTRY_TYPES = new Set([
    'confirmation_response',
    'shift_outcome',
    'voice_call',
]);

function eventEntryLabel(entry: ReplayEntry): string {
    if (entry.entry_type === 'confirmation_response') {
        if (entry.status === 'confirmed') {
            return 'Accepted shift';
        }
        if (entry.status === 'declined') {
            return 'Declined shift';
        }
        return entry.status
            ? `Shift response · ${entry.status}`
            : 'Shift response';
    }
    if (entry.entry_type === 'shift_outcome') {
        const outcome = (entry.status ?? '').replace(/_/g, ' ').trim();
        return outcome ? `Shift ${outcome}` : 'Shift outcome';
    }
    return 'Voice call';
}

function EventEntryRow({ entry }: { readonly entry: ReplayEntry }) {
    return (
        <div className="border-border/60 rounded-md border p-2">
            <div className="flex items-center justify-between gap-2">
                <Badge variant="outline" className="gap-1">
                    {entry.entry_type === 'voice_call' ? (
                        <Phone className="h-3 w-3" />
                    ) : (
                        <ClipboardCheck className="h-3 w-3" />
                    )}
                    {eventEntryLabel(entry)}
                </Badge>
                <span className="text-muted-foreground text-[11px]">
                    {formatCompactDateTime(entry.timestamp)}
                </span>
            </div>
        </div>
    );
}

function PanelSection({
    icon: Icon,
    title,
    loading = false,
    emptyText,
    children,
}: {
    readonly icon: LucideIcon;
    readonly title: string;
    readonly loading?: boolean;
    readonly emptyText: string;
    readonly children: ReactNode;
}) {
    const childArray = Children.toArray(children);
    return (
        <section>
            <div className="text-muted-foreground mb-2 flex items-center gap-1.5 text-xs font-semibold">
                <Icon className="h-3.5 w-3.5" />
                {title}
                {loading ? <Spinner size="sm" /> : null}
            </div>
            {childArray.length > 0 ? (
                <div className="space-y-2">{childArray}</div>
            ) : loading ? null : (
                <p className="text-muted-foreground text-xs">{emptyText}</p>
            )}
        </section>
    );
}

function MemoryEventRow({
    event,
}: {
    readonly event: CaregiverSimMemoryEventRead;
}) {
    return (
        <div className="border-border/60 bg-muted/50 rounded-md border p-2">
            <div className="flex items-center justify-between gap-2">
                <Badge variant="outline" className="capitalize">
                    {event.kind}
                </Badge>
                <span className="text-muted-foreground text-[11px]">
                    importance {event.importance}
                </span>
            </div>
            <p className="text-foreground/80 mt-1 text-xs">
                {event.description}
            </p>
            <p className="text-muted-foreground mt-1 text-[11px]">
                {formatCompactDateTime(event.created_at)}
            </p>
        </div>
    );
}

function RunEventRow({ event }: { readonly event: ReplayDailyEvent }) {
    const isMemory = event.event_type === 'memory_change';
    return (
        <div className="border-border/60 rounded-md border p-2">
            <div className="flex items-center justify-between gap-2">
                <Badge variant="outline" className="gap-1">
                    {isMemory ? (
                        <Brain className="h-3 w-3" />
                    ) : (
                        <UserX className="h-3 w-3" />
                    )}
                    {isMemory ? 'Memory' : 'Callout'}
                </Badge>
                {event.status ? (
                    <span className="text-muted-foreground text-[11px]">
                        {event.status}
                    </span>
                ) : null}
            </div>
            <p className="text-foreground/80 mt-1 text-xs">
                {event.detail ?? ''}
            </p>
        </div>
    );
}

function buildWorldSnapshot(
    replay: CaregiverSimDailyRunReplayResponse | null,
    fraction: number,
    personaShortName: (identifier: string | null | undefined) => string,
): WorldSnapshot {
    if (replay === null) {
        return {
            simulatedTime: 'No run loaded',
            activeCaregivers: '0 caregivers',
            voice: 'No calls',
            recentEvent: 'No recent event',
            nextEvent: 'Pick a run',
        };
    }

    const entries = [...(replay.entries ?? [])].sort(
        (left, right) =>
            getSortableTimestamp(left.timestamp) -
            getSortableTimestamp(right.timestamp),
    );
    if (entries.length === 0) {
        return {
            simulatedTime: formatRunDate(replay.run.local_date),
            activeCaregivers: '0 caregivers',
            voice: 'No calls',
            recentEvent: 'No replay events',
            nextEvent: 'Awaiting activity',
        };
    }

    const startMs = getSortableTimestamp(entries[0]?.timestamp);
    const endMs = getSortableTimestamp(entries[entries.length - 1]?.timestamp);
    const clampedFraction = Math.min(Math.max(fraction, 0), 1);
    const currentMs = startMs + (endMs - startMs) * clampedFraction;
    const recentEntry =
        [...entries]
            .reverse()
            .find(
                (entry) => getSortableTimestamp(entry.timestamp) <= currentMs,
            ) ?? null;
    const nextEntry =
        entries.find(
            (entry) => getSortableTimestamp(entry.timestamp) > currentMs,
        ) ?? null;
    const activeSinceMs = currentMs - 60 * 60 * 1000;
    const activeCaregiverCount = new Set(
        entries
            .filter((entry) => {
                const timestamp = getSortableTimestamp(entry.timestamp);
                return timestamp >= activeSinceMs && timestamp <= currentMs;
            })
            .map((entry) => entry.persona_identifier)
            .filter((identifier): identifier is string => Boolean(identifier)),
    ).size;
    const replayableVoiceCallCount = entries.filter(
        (entry) =>
            entry.entry_type === 'voice_call' &&
            Boolean(entry.recording_url || entry.transcript),
    ).length;

    return {
        simulatedTime: formatCompactUtcDateTime(
            new Date(currentMs).toISOString(),
        ),
        activeCaregivers: `${activeCaregiverCount} ${pluralize(
            activeCaregiverCount,
            'caregiver',
        )}`,
        voice: formatVoiceReplayMetric(replayableVoiceCallCount),
        recentEvent: recentEntry
            ? worldEventSummary(recentEntry, personaShortName)
            : 'Run starting',
        nextEvent: nextEntry
            ? worldEventSummary(nextEntry, personaShortName)
            : 'Run complete',
    };
}

function buildNowWorldSnapshot(
    worldNow: CaregiverSimWorldNowResponse | null,
    loading: boolean,
): WorldSnapshot {
    if (!worldNow) {
        return {
            simulatedTime: loading ? 'Loading now' : 'No current state',
            activeCaregivers: '0 caregivers',
            voice: 'Unknown',
            recentEvent: 'No recent event',
            nextEvent: 'No upcoming event',
        };
    }
    const recentEvent = worldNow.recent_events[0] ?? null;
    const nextEvent = worldNow.upcoming_events[0] ?? null;
    return {
        simulatedTime: formatCompactZonedDateTime(
            worldNow.simulated_at,
            worldNow.organization_timezone,
        ),
        activeCaregivers: `${worldNow.active_caregiver_count} ${pluralize(
            worldNow.active_caregiver_count,
            'caregiver',
        )} · ${worldNow.active_shift_count} ${pluralize(
            worldNow.active_shift_count,
            'shift',
        )}`,
        voice: worldNow.voice.label,
        recentEvent: recentEvent ? recentEvent.label : 'No recent event',
        nextEvent: nextEvent ? nextEvent.label : 'No upcoming event',
    };
}

function worldEventSummary(
    entry: ReplayEntry,
    personaShortName: (identifier: string | null | undefined) => string,
): string {
    const persona = personaShortName(entry.persona_identifier);
    if (entry.entry_type === 'sms') {
        return entry.direction === 'inbound'
            ? `${persona} replied`
            : `Phoebe texted ${persona}`;
    }
    if (entry.entry_type === 'confirmation_sent') {
        return `Phoebe sent confirmation to ${persona}`;
    }
    if (entry.entry_type === 'outreach_response') {
        return `${persona} answered outreach`;
    }
    return `${persona}: ${eventEntryLabel(entry)}`;
}

function formatRunDate(value: string): string {
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
        return value;
    }
    return `${SHORT_MONTHS[month - 1] ?? month} ${day}, ${year}`;
}

function formatCompactUtcDateTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    const month = SHORT_MONTHS[date.getUTCMonth()] ?? date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${month} ${day}, ${hours}:${minutes} UTC`;
}

function pluralize(count: number, singular: string): string {
    return count === 1 ? singular : `${singular}s`;
}

function formatVoiceReplayMetric(count: number): string {
    if (count === 0) {
        return 'No voice replay';
    }
    return `${count} ${pluralize(count, 'voice replay')}`;
}

function buildCaregiverWorldState({
    entries,
    dailyEvents,
    memoryEvents,
}: {
    readonly entries: Array<ReplayEntry>;
    readonly dailyEvents: Array<ReplayDailyEvent>;
    readonly memoryEvents: Array<CaregiverSimMemoryEventRead>;
}): CaregiverWorldState {
    const attentionEvent = dailyEvents.find((event) =>
        ATTENTION_STATUSES.includes(event.status ?? ''),
    );
    if (attentionEvent) {
        return {
            label: 'Needs attention',
            evidence: dailyEventEvidence(attentionEvent),
            tone: 'attention',
            notable: true,
        };
    }

    // A failed/unfilled outcome anywhere in the timeline takes priority over a
    // later routine reply or memory update, so the problem is never masked.
    const attentionEntry = entries.find((entry) =>
        ATTENTION_STATUSES.includes(entry.status ?? ''),
    );
    if (attentionEntry) {
        return {
            label: 'Needs attention',
            evidence: eventEntryLabel(attentionEntry),
            tone: 'attention',
            notable: true,
        };
    }

    // A chat reply is not a change in what a caregiver remembers — only a
    // reflection, a preference, or a remembered event actually updates their
    // memory and should colour the mood.
    const latestMemoryEvent =
        memoryEvents.find((event) =>
            ['thought', 'preference', 'event'].includes(event.kind),
        ) ?? null;
    const latestEntry = sortEntriesDesc(entries)[0] ?? null;
    if (
        latestMemoryEvent &&
        (!latestEntry ||
            getSortableTimestamp(latestMemoryEvent.created_at) >=
                getSortableTimestamp(latestEntry.timestamp))
    ) {
        if (latestMemoryEvent.kind === 'thought') {
            return {
                label: 'Reflective',
                evidence: latestMemoryEvent.description,
                tone: 'settled',
                notable: true,
            };
        }
        if (latestMemoryEvent.kind === 'preference') {
            return {
                label: 'Preference noted',
                evidence: latestMemoryEvent.description,
                tone: 'memory',
                notable: true,
            };
        }
        return {
            label: 'Memory updated',
            evidence: latestMemoryEvent.description,
            tone: 'memory',
            notable: true,
        };
    }

    if (latestEntry) {
        return stateFromReplayEntry(latestEntry);
    }

    const completedDailyEvent = dailyEvents.find((event) =>
        ['detected', 'extracted', 'refilled', 'sms_sent'].includes(
            event.status ?? '',
        ),
    );
    if (completedDailyEvent) {
        return {
            label:
                completedDailyEvent.event_type === 'memory_change'
                    ? 'Memory updated'
                    : 'Resolved',
            evidence: dailyEventEvidence(completedDailyEvent),
            tone:
                completedDailyEvent.event_type === 'memory_change'
                    ? 'memory'
                    : 'settled',
            notable: true,
        };
    }

    return {
        label: 'Observed',
        evidence:
            'No recent replay or persistent memory signal for this caregiver.',
        tone: 'settled',
        notable: false,
    };
}

function stateFromReplayEntry(entry: ReplayEntry): CaregiverWorldState {
    if (entry.entry_type === 'sms') {
        return {
            label:
                entry.direction === 'inbound' ? 'Responded' : 'Awaiting reply',
            evidence: entry.body ?? 'SMS event recorded.',
            tone: entry.direction === 'inbound' ? 'active' : 'settled',
            // Routine chat back-and-forth is not worth a roster badge.
            notable: false,
        };
    }
    if (entry.entry_type === 'voice_call') {
        return {
            label: 'Voice contact',
            evidence: entry.body ?? entry.status ?? 'Voice call recorded.',
            tone: 'active',
            notable: true,
        };
    }
    if (entry.entry_type === 'confirmation_response') {
        return {
            label: 'Confirmation response',
            evidence: entry.status ?? 'Shift confirmation response recorded.',
            tone: 'active',
            notable: true,
        };
    }
    if (entry.entry_type === 'shift_outcome') {
        // Attention-worthy outcomes (e.g. unfilled) are caught earlier in
        // buildCaregiverWorldState; a plain outcome here is a settled signal.
        return {
            label: 'Shift outcome',
            evidence: entry.status ?? 'Shift outcome recorded.',
            tone: 'settled',
            notable: true,
        };
    }
    return {
        label: 'Active',
        evidence: entry.body ?? entry.status ?? `${entry.entry_type} recorded.`,
        tone: 'active',
        notable: true,
    };
}

function dailyEventEvidence(event: ReplayDailyEvent): string {
    const detail = event.detail ?? event.event_type;
    return event.status ? `${detail} - ${event.status}` : detail;
}

function sortEntriesDesc(entries: Array<ReplayEntry>): Array<ReplayEntry> {
    return [...entries].sort(
        (left, right) =>
            getSortableTimestamp(right.timestamp) -
            getSortableTimestamp(left.timestamp),
    );
}

function uniqueMemoryEvents(
    events: Array<CaregiverSimMemoryEventRead>,
): Array<CaregiverSimMemoryEventRead> {
    const seen = new Set<string>();
    const uniqueEvents: Array<CaregiverSimMemoryEventRead> = [];
    for (const event of events) {
        if (seen.has(event.id)) {
            continue;
        }
        seen.add(event.id);
        uniqueEvents.push(event);
    }
    return uniqueEvents;
}

function sortMemoryEventsDesc(
    events: Array<CaregiverSimMemoryEventRead>,
): Array<CaregiverSimMemoryEventRead> {
    return [...events].sort(
        (left, right) =>
            getSortableTimestamp(right.created_at) -
            getSortableTimestamp(left.created_at),
    );
}

function getSortableTimestamp(value: string | null | undefined): number {
    if (!value) {
        return 0;
    }
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
}

function formatCompactDateTime(value: string): string {
    return new Date(value).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function formatCompactZonedDateTime(value: string, timeZone: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return new Intl.DateTimeFormat([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone,
        timeZoneName: 'short',
    }).format(date);
}

function caregiverWorldToneClassName(tone: CaregiverWorldTone): string {
    if (tone === 'attention') {
        return 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300';
    }
    if (tone === 'active') {
        return 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/50 dark:text-blue-300';
    }
    if (tone === 'memory') {
        return 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-900 dark:bg-purple-950/50 dark:text-purple-300';
    }
    return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/50 dark:text-emerald-300';
}
